/**
 * Lifecycle Manager - Singleton for SDK initialization and cleanup
 */

import { SDKInstance, SDKConfig, LifecycleState, Destroyable } from './lifecycle.types'
import { OperationQueue } from './operation-queue'
import { createSDKError } from './sdk-error'
import { ModalSDK } from '../modal/modal.types'
import { AnalyticsEventData } from '../analytics/AnalyticsManager'
import { setBaseUri } from '../util/script-params'
import { DonationChangedMsgFromApp } from '../messenger/messenger.messages'
import { PubsubEvent } from '../pubsub/pubsub'
import type InlineGridProtocolController from '../inline-grid/inline-grid-protocol'

interface ModuleRegistry {
  messenger?: Destroyable
  cookiesManager?: Destroyable
  configService?: Destroyable
  modalCtrl?: Destroyable & { sdk: () => ModalSDK }
  abandonCartNudgeTray?: Destroyable
  crawler?: Destroyable
  analytics?: Destroyable & { sendEvent: (name: string, data: AnalyticsEventData) => void }
  inlineGridProtocol?: InlineGridProtocolController
}

interface SyncableDonationFormElement extends HTMLElement {
  __egHostPageEventSyncDepth?: number
}

const beginHostPageEventSync = (form: SyncableDonationFormElement): void => {
  form.__egHostPageEventSyncDepth = (form.__egHostPageEventSyncDepth || 0) + 1
}

const endHostPageEventSync = (form: SyncableDonationFormElement): void => {
  Promise.resolve().then(() => {
    const nextDepth = (form.__egHostPageEventSyncDepth || 1) - 1

    if (nextDepth > 0) {
      form.__egHostPageEventSyncDepth = nextDepth
      return
    }

    delete form.__egHostPageEventSyncDepth
  })
}

class LifecycleManagerImpl {
  private state: LifecycleState = 'uninitialized'
  private currentInstance: SDKInstance | null = null
  private pendingInit: Promise<SDKInstance> | null = null
  private config: SDKConfig = {}
  private modules: ModuleRegistry = {}
  private queue = new OperationQueue()
  private targetWindow: Window | null = null

  async init(config: SDKConfig = {}): Promise<SDKInstance> {
    if (this.pendingInit) {
      return this.pendingInit
    }

    if (this.state === 'initialized' && this.currentInstance) {
      if (config.onError) {
        config.onError(createSDKError('ALREADY_INITIALIZED', 'SDK is already initialized'))
      }
      return this.currentInstance
    }

    this.config = config
    this.targetWindow = config.win || window
    setBaseUri(config.baseUri)

    this.pendingInit = this.queue.enqueue(async () => {
      try {
        this.state = 'initializing'
        const instance = await this.initializeModules()
        this.state = 'initialized'
        this.currentInstance = instance

        if (config.onReady) {
          config.onReady()
        }

        return instance
      } catch (error) {
        this.state = 'uninitialized'
        const sdkError =
          error instanceof Error
            ? createSDKError('INIT_FAILED', error.message)
            : createSDKError('INIT_FAILED', 'Unknown initialization error')

        if (config.onError) {
          config.onError(sdkError)
        }
        throw sdkError
      } finally {
        this.pendingInit = null
      }
    })

    return this.pendingInit
  }

  destroy(): void {
    if (this.state === 'uninitialized' || this.state === 'destroying') {
      return
    }

    this.state = 'destroying'

    try {
      this.cleanupModules()

      // Clean up modal and analytics from window.eg, but keep lifecycle methods
      if (this.targetWindow?.eg) {
        delete this.targetWindow.eg.modal
        delete this.targetWindow.eg.sendAnalyticsEvent
      }

      this.currentInstance = null
      this.modules = {}
      this.state = 'uninitialized'
    } catch (error) {
      this.state = 'uninitialized'
    }
  }

  isInitialized(): boolean {
    return this.state === 'initialized'
  }

  getState(): LifecycleState {
    return this.state
  }

  private async initializeModules(): Promise<SDKInstance> {
    const win = this.targetWindow!

    // Dynamic imports to avoid circular dependencies
    const { default: Messenger } = await import('../messenger/messenger')
    const { default: CookiesManager } = await import('../cookies/CookiesManager')
    const { default: ConfigService } = await import('../config/config.service')
    const { default: ModalCtrl } = await import('../modal/modal.ctrl')
    const { default: AbandonCartNudgeTrayCtrl } = await import(
      '../abandon-cart-nudge-tray/abandon-cart-nudge-tray.ctrl'
    )
    const { default: Crawler } = await import('../crawler/crawler')
    const { default: AnalyticsManager } = await import('../analytics/AnalyticsManager')
    const { createHostPageEventInstrumentation } = await import(
      '../host-page-events/host-page-event.instrumentation'
    )
    const { makeInlineGridIframeRenderer } = await import('../inline-grid/inline-grid-iframe')
    const { default: InlineGridProtocolController } = await import(
      '../inline-grid/inline-grid-protocol'
    )
    const { strategyHandlers } = await import('../crawler/crawler.element.strategies')

    const msg = new Messenger({ win })
    this.modules.messenger = msg as unknown as Destroyable

    const cookiesManager = new CookiesManager()
    this.modules.cookiesManager = cookiesManager as unknown as Destroyable
    const pgdid = cookiesManager.getPgdidCookie() ?? undefined

    const configService = new ConfigService({ win, cookiesManager })
    const configs = configService.fetch()
    this.modules.configService = configService as unknown as Destroyable

    const analytics = new AnalyticsManager({ win })
    analytics.initialize()
    analytics.setupEventListener()
    this.modules.analytics = analytics as unknown as Destroyable & {
      sendEvent: (name: string, data: AnalyticsEventData) => void
    }

    const modalCtrl = new ModalCtrl({
      getInlineGridParams: (campaignId) =>
        this.modules.inlineGridProtocol?.getSelectionParams(campaignId),
      win,
      msg,
      configs,
      pgdid,
      sendAnalyticsEvent: (eventName, eventData) => analytics.sendEvent(eventName, eventData),
    })
    this.modules.modalCtrl = modalCtrl as unknown as Destroyable & { sdk: () => ModalSDK }
    const modalSDK = modalCtrl.sdk()

    const inlineGridProtocol = new InlineGridProtocolController({
      hostPageEventInstrumentation: modalCtrl.getHostPageEventInstrumentation(),
      recordTelemetry: modalCtrl.recordInlineGridTelemetry,
      modalSDK,
      win,
    })
    this.modules.inlineGridProtocol = inlineGridProtocol
    const inlineGridRenderers = {
      iframe: makeInlineGridIframeRenderer({
        fallback: strategyHandlers.donationForm,
        protocol: inlineGridProtocol,
      }),
    }

    // Per-frequency amount memory: when the user switches frequency in the
    // modal, we remember the last selected amount for each frequency so we
    // can restore it when they switch back.
    // Key: `${campaignId}:${frequency}`, Value: numeric amount
    const amountByFrequency: Record<string, number> = {}
    // Track the current frequency per campaign so we can detect frequency changes
    const currentFrequency: Record<string, string> = {}

    // Sync donation changes from the EG3 iframe back to the donation grid.
    // When the user modifies amount/frequency inside the modal, the embedded
    // app sends DONATION_CHANGED_MSG_FROM_APP.  We find the matching
    // <cl-donation-form> by campaign-id and push the updated values into it.
    msg.onDonationChanged((event: PubsubEvent<DonationChangedMsgFromApp>) => {
      // Ignore DONATION_CHANGED messages from hidden iframes.
      // During preload/cached reuse the EG3 app can emit DONATION_CHANGED
      // from an iframe that is not the visible checkout. If we process those
      // messages, the user's existing donation-grid selection can be
      // overwritten by a background campaign. Only sync the open campaign.
      if (!modalSDK.isOpen()) {
        return
      }

      const { campaignId, params } = event.data.payload

      if (!campaignId) {
        return
      }

      if (modalSDK.getCampaign() !== campaignId) {
        return
      }

      inlineGridProtocol.syncSelection(campaignId, params)

      const form = document.querySelector(
        `cl-donation-form[campaign-id="${campaignId}"]`
      ) as HTMLElement | null

      if (!form) {
        return
      }

      // The <cl-donation-form> web component exposes `selectedFrequency`
      // and `selectedAmount` setters that trigger internal re-renders.
      const formAny = form as any
      beginHostPageEventSync(form as SyncableDonationFormElement)

      try {
        // Sync currency BEFORE amount/frequency.  Setting selectedCurrency
        // triggers the currency_selected event, which asynchronously fetches
        // the exchange rate and converts stored custom "Other" amounts from
        // the OLD currency.  If we set the amount first, the web component
        // fires amount_selected which overwrites the stored value with the
        // already-converted amount under the wrong currency key, causing a
        // double-conversion when the currency_selected handler runs later.
        // By changing currency first, the handler reads the correct old
        // value, and the subsequent amount set stores under the new (correct)
        // currency.  (PRO-2463)
        if (params.currency) {
          const currentCurrency =
            (typeof formAny.getCurrentCurrency === 'function'
              ? formAny.getCurrentCurrency()
              : null) || 'USD'

          if (params.currency !== currentCurrency) {
            formAny.selectedCurrency = params.currency
          }
        }

        const prevFreq = currentFrequency[campaignId] || formAny.selectedFrequency || 'one-time'
        const newFreq = params.frequency || prevFreq
        const frequencyChanged = params.frequency && params.frequency !== prevFreq

        // Parse the amount from the message
        let numericAmount: number | null = null
        if (params.amount !== undefined && params.amount !== null) {
          const parsed = typeof params.amount === 'string' ? Number(params.amount) : params.amount
          if (!isNaN(parsed) && parsed > 0) {
            numericAmount = parsed
          }
        }

        if (frequencyChanged) {
          // When frequency changes, the amount in the message belongs to the
          // OLD frequency (the embedded app sends the stale value).  Save it
          // for the old frequency, then restore the saved amount for the new one.
          if (numericAmount) {
            amountByFrequency[`${campaignId}:${prevFreq}`] = numericAmount
          }

          currentFrequency[campaignId] = params.frequency!
          formAny.selectedFrequency = params.frequency

          const savedAmount = amountByFrequency[`${campaignId}:${params.frequency}`]
          if (savedAmount) {
            // Reset first to ensure the setter fires even if the numeric
            // value hasn't changed (the web component guards on #t !== e).
            formAny.selectedAmount = 0
            formAny.selectedAmount = savedAmount
          }
        } else {
          // Same frequency — the user changed the amount.  Store and apply it.
          if (numericAmount) {
            amountByFrequency[`${campaignId}:${newFreq}`] = numericAmount

            // Determine whether the amount matches a preset button by
            // inspecting the actual <cl-custom-button> elements inside the
            // <cl-amount-selector>.  We cannot use formAny.config because
            // the cl-donation-form web component has no config getter.
            const amountSelector = formAny.shadowRoot?.querySelector('cl-amount-selector')
            let isPreset = false
            if (amountSelector?.shadowRoot) {
              const buttons = amountSelector.shadowRoot.querySelectorAll('cl-custom-button')
              buttons.forEach((btn: Element) => {
                const val = btn.getAttribute('value')
                if (val !== null && Math.round(Number(val)) === Math.round(numericAmount!)) {
                  isPreset = true
                }
              })
            }

            // The cl-donation-form's selectedAmount setter guards on
            // #t !== e, so we reset to 0 first to force the guard to pass.
            formAny.selectedAmount = 0
            formAny.selectedAmount = numericAmount

            // Fix up the cl-amount-selector's visual state.  The
            // cl-donation-form's selectedAmount setter never resets
            // isCustomAmountActive, so we must do it ourselves.
            if (amountSelector) {
              amountSelector.isCustomAmountActive = !isPreset
              amountSelector.selectedAmount = numericAmount
            }

            if (!isPreset) {
              formAny.externalCustomAmount = { frequency: newFreq, amount: numericAmount }
            }
          }

          if (params.frequency) {
            currentFrequency[campaignId] = params.frequency
            formAny.selectedFrequency = params.frequency
          }
        }
      } finally {
        endHostPageEventSync(form as SyncableDonationFormElement)
      }
    })

    // Keep existing host-page surfaces on their established transport. The iframe grid
    // uses ModalCtrl's campaign-aware queue above because it has no legacy DOM messenger.
    const hostPageEventInstrumentation = createHostPageEventInstrumentation({
      sendAnalyticsEvent: (eventName, eventData) => analytics.sendEvent(eventName, eventData),
      sendMessage: msg.iframe.sendMessage,
    })

    const abandonCartNudgeTray = new AbandonCartNudgeTrayCtrl({
      win,
      msg,
      modalSDK,
      configs,
      hostPageEventInstrumentation,
    })
    this.modules.abandonCartNudgeTray = abandonCartNudgeTray as unknown as Destroyable

    const crawler = new Crawler({
      win,
      modalSDK,
      configs,
      hostPageEventInstrumentation,
      inlineGridRenderers,
      pgdid,
    })
    this.modules.crawler = crawler as unknown as Destroyable

    const instance = this.createSDKInstance(modalSDK, analytics)

    // Extend window.eg with modal and analytics, preserving lifecycle methods
    const egApi = (win as any).eg
    if (egApi) {
      egApi.modal = this.createProxiedModal(modalSDK)
      egApi.sendAnalyticsEvent = this.createProxiedAnalytics(analytics)
    }

    return instance
  }

  private createSDKInstance(modalSDK: ModalSDK, analytics: any): SDKInstance {
    return {
      modal: modalSDK,
      sendAnalyticsEvent: (name: string, data: AnalyticsEventData) => {
        if (!this.isInitialized()) {
          throw createSDKError('NOT_INITIALIZED', 'SDK has been destroyed')
        }
        analytics.sendEvent(name, data)
      },
      destroy: () => this.destroy(),
      reinit: (newConfig?: SDKConfig) => {
        this.destroy()
        return this.initSync(newConfig || this.config)
      },
      isInitialized: () => this.isInitialized(),
    }
  }

  private initSync(config: SDKConfig): SDKInstance {
    this.init(config)
    return this.currentInstance!
  }

  private createProxiedModal(modalSDK: ModalSDK): ModalSDK {
    const isInitialized = () => this.isInitialized()
    return new Proxy(modalSDK, {
      get(target, prop: string | symbol) {
        if (!isInitialized()) {
          throw createSDKError('NOT_INITIALIZED', 'SDK has been destroyed')
        }
        return (target as unknown as Record<string | symbol, unknown>)[prop]
      },
    })
  }

  private createProxiedAnalytics(analytics: any) {
    return (name: string, data: AnalyticsEventData) => {
      if (!this.isInitialized()) {
        throw createSDKError('NOT_INITIALIZED', 'SDK has been destroyed')
      }
      analytics.sendEvent(name, data)
    }
  }

  private cleanupModules(): void {
    const cleanupOrder: (keyof ModuleRegistry)[] = [
      'abandonCartNudgeTray',
      'crawler',
      'inlineGridProtocol',
      'analytics',
      'modalCtrl',
      'configService',
      'cookiesManager',
      'messenger',
    ]

    for (const key of cleanupOrder) {
      const module = this.modules[key]
      if (module && typeof module.destroy === 'function') {
        try {
          module.destroy()
        } catch (e) {
          console.error(`[eg] Failed to destroy ${key}:`, e)
        }
      }
    }
  }
}

export const LifecycleManager = new LifecycleManagerImpl()
